import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal, IonPage } from "@ionic/react";
import { Helmet } from "react-helmet";

const BiggestNumber: React.FC = () => {
    const [numbers, setNumbers] = useState<number[]>([]);
    const [correctAnswer, setCorrectAnswer] = useState<number>(0);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Biggest Number Worksheets for UKG | Compare Numbers Easily";
    }, []);

    const generateNewQuestion = () => {
        const randomNumbers = new Set<number>();

        // Generate 6 unique random numbers between 11 and 100
        while (randomNumbers.size < 6) {
            randomNumbers.add(Math.floor(Math.random() * (100 - 11 + 1)) + 11);
        }

        const numArray = Array.from(randomNumbers);
        const biggest = Math.max(...numArray);

        setNumbers(numArray);
        setCorrectAnswer(biggest);
        setMessage("");
    };

    const checkAnswer = (selected: number) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG students learn to find the biggest number through fun worksheets and activities. Boost number sense and comparison skills with free printable resources." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/biggest-number" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Biggest Number</h1>

                                    <p>
                                        Finding the <strong>biggest number</strong> in a group helps UKG children build early math skills such as comparing values, ordering numbers, and preparing for operations like addition and subtraction.
                                    </p>

                                    <h2>🔍 What is the Biggest Number?</h2>
                                    <p>
                                        When we look at two or more numbers, the <strong>biggest number</strong> is the one with the highest value.
                                    </p>
                                    <p><strong>Example:</strong> Among 4, 9, and 7 → The biggest number is <strong>9</strong>.</p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Identify the biggest number from a given set</li>
                                        <li>Improve number recognition and comparison</li>
                                        <li>Develop logical thinking and problem-solving</li>
                                        <li>Lay the foundation for ordering numbers</li>
                                    </ul>

                                    <h2>📝 Worksheet Activities</h2>
                                    <ul>
                                        <li>Circle the biggest number</li>
                                        <li>Match objects with their greatest value</li>
                                        <li>Color the object with the biggest number</li>
                                        <li>Fill in blanks with the biggest value</li>
                                        <li>Interactive drag-and-drop comparisons</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/addition"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Addition</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>👩‍🏫 Teaching Tips</h2>
                                    <ul>
                                        <li>Use real objects like fruits, blocks, or stickers to compare quantities</li>
                                        <li>Play games: “Who has the biggest number card?” or “Find the largest!”</li>
                                        <li>Show numbers on a number line to visualize comparisons</li>
                                        <li>Use stories or characters who “win” by having the biggest number</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Biggest Number</h1>

                                <p>
                                    Finding the <strong>biggest number</strong> in a group helps UKG children build early math skills such as comparing values, ordering numbers, and preparing for operations like addition and subtraction.
                                </p>

                                <h2>🔍 What is the Biggest Number?</h2>
                                <p>
                                    When we look at two or more numbers, the <strong>biggest number</strong> is the one with the highest value.
                                </p>
                                <p><strong>Example:</strong> Among 4, 9, and 7 → The biggest number is <strong>9</strong>.</p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify the biggest number from a given set</li>
                                    <li>Improve number recognition and comparison</li>
                                    <li>Develop logical thinking and problem-solving</li>
                                    <li>Lay the foundation for ordering numbers</li>
                                </ul>

                                <h2>📝 Worksheet Activities</h2>
                                <ul>
                                    <li>Circle the biggest number</li>
                                    <li>Match objects with their greatest value</li>
                                    <li>Color the object with the biggest number</li>
                                    <li>Fill in blanks with the biggest value</li>
                                    <li>Interactive drag-and-drop comparisons</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/addition"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Addition</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>👩‍🏫 Teaching Tips</h2>
                                <ul>
                                    <li>Use real objects like fruits, blocks, or stickers to compare quantities</li>
                                    <li>Play games: “Who has the biggest number card?” or “Find the largest!”</li>
                                    <li>Show numbers on a number line to visualize comparisons</li>
                                    <li>Use stories or characters who “win” by having the biggest number</li>
                                </ul>
                            </article>
                            <div style={{ fontSize: "24px", marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                                {numbers.map((num, index) => (
                                    <div className="background-blue" key={index} onClick={() => checkAnswer(num)} style={{ margin: "5px", padding: "10px" }}>
                                        {num}
                                    </div>
                                ))}
                            </div>
                            <p style={{ fontSize: "20px", marginTop: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default BiggestNumber;
